import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Alert, AlertProps, Snackbar } from '@mui/material'

export interface IMessageAlert {
  content: string
  duration?: number
  severity?: AlertProps['severity']
}

interface IToastContext {
  handler: (params: IMessageAlert) => void
}

let toastHandler: ((params: IMessageAlert) => void) | null = null

export const handler = (params: IMessageAlert) => {
  if (toastHandler) {
    toastHandler(params)
  }
}

const ToastContext = createContext<IToastContext>({} as IToastContext)

const ToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [params, setParams] = useState<IMessageAlert>({} as IMessageAlert)

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handler = useCallback<IToastContext['handler']>(
    ({ content, duration, severity }) => {
      setParams({
        content,
        duration: duration ?? 8000,
        severity: severity ?? 'success'
      })
      setOpen(true)
    },
    []
  )

  useEffect(() => {
    toastHandler = handler
  }, [handler])

  const contextValue = useMemo(() => ({ handler }), [handler])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <Snackbar
        open={open}
        autoHideDuration={params.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        color='danger'
      >
        <Alert onClose={handleClose} severity={params.severity}>
          {params.content}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}

export default ToastProvider
